// Main App component
import { useState } from 'react';
import { useGame } from './hooks/useGame';
import { useGameApi } from './hooks/useGameApi';
import { Layout } from './components/Layout/Layout';
import { NewGame } from './components/NewGame/NewGame';
import { GameView } from './components/GameView/GameView';
import { Settings, DEFAULT_SETTINGS, type GameSettings } from './components/Settings/Settings';
import { LoadingSpinner } from './components/LoadingSpinner/LoadingSpinner';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import './styles/global.css';

export default function App() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState<GameSettings>(DEFAULT_SETTINGS);
  const api = useGameApi();
  const {
    phase,
    sessionId,
    story,
    character,
    currentScene,
    choices,
    sceneHistory,
    waitingForRoll,
    pendingChoiceIndex,
    loading,
    error,
    startNewGame,
    makeChoice,
    submitChoice,
    completeGame,
    abandonGame,
    startNewGameFromAbandoned,
    updateCharacter,
    leveledUp,
    previousLevel,
    clearLevelUp,
  } = useGame();

  const handleStartGame = async (
    theme: string,
    characterName: string,
    characterClass: import('./types').CharacterClass
  ) => {
    await startNewGame(theme, characterName, characterClass);
  };

  const handleMakeChoice = async (index: number) => {
    await makeChoice(index);
  };

  const handleCharacterUpdate = (updatedCharacter: import('./types').Character) => {
    updateCharacter(updatedCharacter);
  };

  const handleRoll = async (result: number, modifier: number, statAbbrev: string) => {
    if (pendingChoiceIndex !== null) {
      await submitChoice(pendingChoiceIndex, result, modifier, statAbbrev);
    }
  };

  const handleSettingsClick = () => {
    setSettingsOpen(true);
  };

  const handleSettingsSave = (newSettings: GameSettings) => {
    setSettings(newSettings);
    setSettingsOpen(false);
  };

  const renderContent = () => {
    if (error) {
      return (
        <div className="error-message">
          <p>Error: {error}</p>
          <button className="btn" onClick={startNewGameFromAbandoned}>
            Start New Game
          </button>
        </div>
      );
    }

    switch (phase) {
      case 'new':
        return <NewGame onStartGame={handleStartGame} loading={loading} />;

      case 'playing':
        if (!story || !character || !currentScene) {
          return <div className="loading">Loading...</div>;
        }
        return (
          <GameView
            story={story}
            character={character}
            currentScene={currentScene}
            choices={choices}
            sceneHistory={sceneHistory}
            settings={settings}
            getPromptLog={api.getPromptLog}
            leveledUp={leveledUp}
            previousLevel={previousLevel}
            onLevelUpDismiss={clearLevelUp}
            onMakeChoice={handleMakeChoice}
            onRoll={handleRoll}
            onNewGame={startNewGameFromAbandoned}
            onCharacterUpdate={handleCharacterUpdate}
            loading={loading}
          />
        );

      case 'complete':
        return (
          <div className="game-complete">
            <h2>Adventure Complete!</h2>
            <p>Thank you for playing. Your story has been saved.</p>
            <div className="complete-actions">
              <button className="btn btn-primary" onClick={startNewGameFromAbandoned}>
                Play Again
              </button>
            </div>
          </div>
        );

      case 'abandoned':
        return (
          <div className="game-abandoned">
            <h2>Adventure Abandoned</h2>
            <p>Your story has been saved for later.</p>
            <div className="abandoned-actions">
              <button className="btn" onClick={startNewGameFromAbandoned}>
                Start New Game
              </button>
              {sessionId && (
                <button className="btn btn-primary" onClick={() => window.location.reload()}>
                  Continue This Story
                </button>
              )}
            </div>
          </div>
        );

      default:
        return <NewGame onStartGame={handleStartGame} loading={loading} />;
    }
  };

  return (
    <ErrorBoundary>
      <Layout 
        storyTitle={phase === 'playing' ? story?.title : undefined} 
        onSettingsClick={handleSettingsClick}
        onAbandon={phase === 'playing' ? abandonGame : undefined}
        onComplete={phase === 'playing' ? completeGame : undefined}
      >
        {renderContent()}
      </Layout>
      <LoadingSpinner 
        isOpen={loading} 
        message={phase === 'new' ? 'Creating your adventure...' : 'Loading...'}
      />
      {settingsOpen && (
        <Settings
          settings={settings}
          onSave={handleSettingsSave}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </ErrorBoundary>
  );
}
